import { RefObject, useCallback, useEffect, useRef, useState } from 'react';
import { useLazyListMedicalMatchQuestionsQuery } from '@/api';
import type { MedicalMatchItem, MedicalMatchQuestion } from '@/api';
import { LIVE_ANSWER_FLASH_DURATION_MS } from './constants';
import { getAnswerEntryIds } from './utils';

interface UseLiveAnswerScrollProps {
  /** Case whose live questions are looked up; unset leaves the lookup empty. */
  caseId: string | undefined;
  /** The panel's answers, as held in the store. */
  sections: MedicalMatchItem[];
  /** Set while the panel shows its loading placeholder in place of the answers. */
  isLoading: boolean;
  /** The panel's search, normalised; a change can bring hidden answers on screen. */
  searchQuery: string;
  /** The panel root, whose parent is the sidebar's scroll container. */
  rootRef: RefObject<HTMLDivElement | null>;
  /** The sticky search bar, which sits over the top of that container. */
  searchBarRef: RefObject<HTMLDivElement | null>;
}

interface LiveAnswerScroll {
  /** Entries just scrolled to; empty again once their glow has played out. */
  flashEntryIds: ReadonlySet<string>;
  /**
   * Reads which live questions have been answered inside the look-back the queue
   * hands over. Never rejects: a failed lookup costs the scroll, and the answer
   * refresh that follows it must not be held up by it.
   */
  findAnsweredQuestions: (lookBackSeconds: number) => Promise<MedicalMatchQuestion[]>;
  /** Takes the questions found, once their answers are in the store; ones already shown are ignored. */
  scrollToAnswers: (questions: MedicalMatchQuestion[]) => void;
}

const NO_ENTRY_IDS: ReadonlySet<string> = new Set();

/**
 * Brings live answers into view once they land: scrolls the panel to the first
 * of the new entries and marks each of them briefly, the way the chronology
 * page lands a cross-tab jump.
 *
 * Which entries answer a question is `getAnswerEntryIds`' call. Only entries on
 * screen are scrolled to: an answer the search is hiding is held rather than
 * the search being cleared under the user, and brought into view once the
 * search lets it back on screen.
 */
export function useLiveAnswerScroll({
  caseId,
  sections,
  isLoading,
  searchQuery,
  rootRef,
  searchBarRef,
}: UseLiveAnswerScrollProps): LiveAnswerScroll {
  const [listQuestions] = useLazyListMedicalMatchQuestionsQuery();
  // State rather than a ref so answers reported while the loading placeholder
  // is up still re-run the scroll once the entries are back on screen.
  const [pendingQuestions, setPendingQuestions] = useState<MedicalMatchQuestion[] | null>(null);
  const [flashEntryIds, setFlashEntryIds] = useState(NO_ENTRY_IDS);

  // Each lookup's margin overlaps the one before it, so an answer can come back
  // again with the next one to land. Remembering the ones already shown stops
  // the panel jumping back to the earlier answer.
  const shownQuestionIdsRef = useRef(new Set<string>());
  // Answered, in the store, but filtered out by the search when they landed.
  // A ref, as holding them mustn't itself re-run the scroll; the search
  // changing is what does.
  const hiddenQuestionsRef = useRef<MedicalMatchQuestion[]>([]);

  const findAnsweredQuestions = useCallback(
    async (lookBackSeconds: number) => {
      if (!caseId) return [];

      try {
        const { questions } = await listQuestions({
          caseId,
          askedWhen: ['LIVE'],
          status: ['ANSWERED'],
          maxTimeSinceUpdatedSeconds: lookBackSeconds,
        }).unwrap();
        return questions;
      } catch (error) {
        console.error('Failed to check for answered live Medical Match questions:', error);
        return [];
      }
    },
    [caseId, listQuestions],
  );

  const scrollToAnswers = useCallback((questions: MedicalMatchQuestion[]) => {
    const added = questions.filter(({ questionId }) => !shownQuestionIdsRef.current.has(questionId));
    if (added.length === 0) return;

    setPendingQuestions((pending) => [...(pending ?? []), ...added]);
  }, []);

  useEffect(() => {
    if (isLoading || (!pendingQuestions && hiddenQuestionsRef.current.length === 0)) return;
    const scrollEl = rootRef.current?.parentElement;
    if (!scrollEl) return;

    // Keyed by id: a held question can come back with the next lookup too.
    const queued = new Map(
      [...hiddenQuestionsRef.current, ...(pendingQuestions ?? [])].map((question) => [question.questionId, question]),
    );
    hiddenQuestionsRef.current = [];
    if (pendingQuestions) setPendingQuestions(null);

    const answeredEntryIds = [...queued.values()].flatMap((question) => {
      // Only counted as shown once its entries turn up, so a question whose
      // answer isn't in the store yet is tried again on the next lookup.
      const entryIds = getAnswerEntryIds(sections, question);
      if (entryIds.length === 0) return [];

      const onScreenEntryIds = entryIds.filter((entryId) => scrollEl.querySelector(`[data-entry-id="${entryId}"]`));
      if (onScreenEntryIds.length === 0) {
        hiddenQuestionsRef.current.push(question);
        return [];
      }
      shownQuestionIdsRef.current.add(question.questionId);
      return onScreenEntryIds;
    });
    if (answeredEntryIds.length === 0) return;

    // One selector list, so the match is whichever answer comes first on screen.
    const entryEl = scrollEl.querySelector<HTMLElement>(
      answeredEntryIds.map((entryId) => `[data-entry-id="${entryId}"]`).join(', '),
    );
    if (!entryEl) return;

    // Scrolled on the sidebar's container itself rather than via
    // `scrollIntoView`, which would move any scrollable ancestor too, and
    // offset by the sticky search bar so the answer doesn't land underneath it.
    const entryTop = entryEl.getBoundingClientRect().top - scrollEl.getBoundingClientRect().top + scrollEl.scrollTop;
    scrollEl.scrollTo({ top: entryTop - (searchBarRef.current?.offsetHeight ?? 0), behavior: 'smooth' });
    setFlashEntryIds(new Set(answeredEntryIds));
    // `searchQuery` isn't read here, but its changing is what lets a held answer
    // back on screen.
  }, [isLoading, pendingQuestions, rootRef, searchBarRef, searchQuery, sections]);

  useEffect(() => {
    if (flashEntryIds.size === 0) return;
    const timeoutId = setTimeout(() => setFlashEntryIds(NO_ENTRY_IDS), LIVE_ANSWER_FLASH_DURATION_MS);
    return () => clearTimeout(timeoutId);
  }, [flashEntryIds]);

  return { flashEntryIds, findAnsweredQuestions, scrollToAnswers };
}
